import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import { Button } from '@spotify-internal/encore-web';

import styles from './ExpandableContent.module.css';

export type ExpandableContentProps = {
  children: ReactNode;
  className?: string;
  collapsedHeight?: string;
  fadeBackground?: string;
  fadeHeight?: string;
};

export const ExpandableContent = ({
  children,
  className,
  collapsedHeight = '6em',
  fadeBackground = 'var(--background-base)',
  fadeHeight = 'var(--encore-spacing-looser-2)',
}: ExpandableContentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (!container || !content || isExpanded) {
      return undefined;
    }

    const updateIsExpandable = () => {
      setIsExpandable(content.scrollHeight > container.clientHeight);
    };

    updateIsExpandable();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateIsExpandable);
    resizeObserver.observe(content);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isExpanded]);

  return (
    <div className={styles.root}>
      <div
        ref={containerRef}
        className={classNames(styles.content, className, {
          [styles.collapsed]: !isExpanded,
          [styles.expanded]: isExpanded,
        })}
        style={{ '--collapsed-height': collapsedHeight } as CSSProperties}
      >
        <div ref={contentRef}>{children}</div>
        {!isExpanded && isExpandable && (
          <span
            className={styles.fade}
            style={
              {
                '--fade-background': fadeBackground,
                '--fade-height': fadeHeight,
              } as CSSProperties
            }
            aria-hidden
          />
        )}
      </div>
      {isExpandable && (
        <Button
          type="button"
          variant="tertiary"
          size="small"
          isCondensedInline
          aria-expanded={isExpanded}
          onClick={() => {
            setIsExpanded(currentIsExpanded => !currentIsExpanded);
          }}
         data-track-source-anchor="7f6ad49f58;expandable-content-button">
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  );
};
